import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../../lib/axios';
import { ImSpinner3 } from 'react-icons/im';
import { API_BASE_URL } from '../../lib/constants';

const LoadingSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-teal-800 bg-opacity-50 z-50">
    <ImSpinner3 className="animate-spin text-white text-4xl" />
    <p className="mt-4 text-white">Checking access...</p>
  </div>
);

const ProtectedRoute = ({ allowedRoles = [], allowedPermissions = [], children }) => {
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [permissions, setPermissions] = useState([]);


  useEffect(() => {
    const checkAuth = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');

      if (token) {
        setIsAuthenticated(true);
        setUserRole('TokenAccess');
        setPermissions([]);

        setAuthChecked(true);
        return;
      }

      try {
        const res = await api.get('/auth/verify');

        if (res.data.status === 'authenticated') {
          console.log('[ProtectedRoute] Auth verified:', res.data.role);
          setIsAuthenticated(true);
          setUserRole(res.data.role);
          setPermissions(res.data.permissions || []);
        } else {
          console.log('[ProtectedRoute] Auth failed: Not authenticated');
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('[ProtectedRoute] Auth check error:', err);
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [location.search]);

  if (!authChecked) return <LoadingSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Case-insensitive role check
  const normalizedUserRole = userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1).toLowerCase() : null;
  const isAdmin = normalizedUserRole === 'Admin';

  if (isAdmin) return children;

  const isTokenAccess = normalizedUserRole === 'TokenAccess';

  // Check specific permissions if provided
  if (!isTokenAccess && allowedPermissions.length) {
    const hasPermission = allowedPermissions.some(p => 
      (permissions || []).map(per => per.toLowerCase()).includes(p.toLowerCase())
    );
    if (hasPermission) return children;
  }

  // Fallback to role check
  if (!isTokenAccess && allowedRoles.length) {
    const hasRole = allowedRoles.some(r => r.toLowerCase() === (userRole || '').toLowerCase());
    if (hasRole) return children;
    
    return <Navigate to="/unauthorized" replace />;
  }

  // If permissions were required but none matched
  if (!isTokenAccess && allowedPermissions.length) {
     return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
